import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Spinner from 'react-bootstrap/Spinner';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { BASE_URL } from '../Config';

export default function ContentFirst() {
  const navigate = useNavigate();
  const [counts, setCounts] = useState({
    registered: 0,
    total: 0,
    old: 0,
    new: 0,
    paid: 0,
    pending_payment: 0,
    recent_members: [],
    area_stats: []
  });
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');

  const loadCounts = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/getall`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = res.data || {};
      setCounts({
        registered: Number(data.registered || 0),
        total: Number(data.total || data.registered || 0),
        old: Number(data.old || 0),
        new: Number(data.new || 0),
        paid: Number(data.paid || 0),
        pending_payment: Number(data.pending_payment || 0),
        recent_members: data.recent_members || [],
        area_stats: data.area_stats || []
      });
    } catch (e) {
      console.error('Failed to load counts', e);
      Swal.fire({
        icon: 'error',
        title: 'Fetch Error',
        text: 'Could not load dashboard metrics from backend API.'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    loadCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleExportCSV = async () => {
    try {
      Swal.fire({
        title: 'Exporting Data...',
        text: 'Please wait while we generate the CSV file.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const response = await axios.get(`${BASE_URL}/api/members/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `association_members_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);

      Swal.fire({
        icon: 'success',
        title: 'Export Successful',
        text: 'Members spreadsheet downloaded successfully.'
      });
    } catch (e) {
      console.error('Export failed', e);
      Swal.fire({
        icon: 'error',
        title: 'Export Failed',
        text: 'An error occurred while compiling the CSV download.'
      });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${d.getDate().toString().padStart(2, '0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
    } catch (e) {
      return dateStr;
    }
  };

  const renderValue = (value) =>
    loading ? (
      <Spinner animation="border" size="sm" variant="primary" />
    ) : (
      <span className="h3 mb-0 font-weight-bold text-gray-800">{value}</span>
    );

  const colors = ['#3f51b5', '#7b1fa2', '#43a047', '#fb8c00', '#00acc1'];

  return (
    <div className="container-fluid dashboard-container">
      {/* Page Heading */}
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <div>
          <h1 className="h3 mb-0 text-gray-800 font-weight-bold">Overview</h1>
          <p className="text-muted mb-0">Banarasi Vastra Udyog Association admin center</p>
        </div>
        <button
          onClick={loadCounts}
          className="d-none d-sm-inline-block btn btn-sm btn-light shadow-sm text-primary font-weight-bold"
        >
          <i className="fas fa-sync-alt fa-sm mr-2"></i> Refresh Stats
        </button>
      </div>

      {/* Metrics Row */}
      <div className="row">
        {/* Total Members */}
        <div className="col-xl-3 col-md-6 mb-4">
          <Link to="/members" className="text-decoration-none">
            <div className="card glass-metric-card border-primary h-100 py-2">
              <div className="card-body">
                <div className="row no-gutters align-items-center">
                  <div className="col mr-2">
                    <div className="text-xs font-weight-bold text-primary text-uppercase mb-1">
                      Total Members
                    </div>
                    {renderValue(counts.total)}
                    <div className="text-xs text-muted mt-1">All approved members</div>
                  </div>
                  <div className="col-auto">
                    <div className="metric-icon-box icon-bg-primary">
                      <i className="fas fa-users"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Link>
        </div>

        {/* New Registrations */}
        <div className="col-xl-3 col-md-6 mb-4">
          <Link to="/new-members" className="text-decoration-none">
            <div className="card glass-metric-card border-purple h-100 py-2">
              <div className="card-body">
                <div className="row no-gutters align-items-center">
                  <div className="col mr-2">
                    <div className="text-xs font-weight-bold text-purple text-uppercase mb-1 d-flex align-items-center">
                      New Members
                      {!loading && counts.new > 0 && (
                        <span className="pulse-badge-creative ml-2">Action</span>
                      )}
                    </div>
                    {renderValue(counts.new)}
                    <div className="text-xs text-muted mt-1">Awaiting admin verification</div>
                  </div>
                  <div className="col-auto">
                    <div className="metric-icon-box icon-bg-purple">
                      <i className="fas fa-user-plus"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Link>
        </div>

        {/* Paid Members */}
        <div className="col-xl-3 col-md-6 mb-4">
          <Link to="/members" className="text-decoration-none">
            <div className="card glass-metric-card border-success h-100 py-2">
              <div className="card-body">
                <div className="row no-gutters align-items-center">
                  <div className="col mr-2">
                    <div className="text-xs font-weight-bold text-success text-uppercase mb-1">
                      Cleared Dues
                    </div>
                    {renderValue(counts.paid)}
                    <div className="text-xs text-muted mt-1">
                      {!loading && counts.total > 0
                        ? `${Math.round((counts.paid / counts.total) * 100)}% of members cleared`
                        : 'Fees active'}
                    </div>
                  </div>
                  <div className="col-auto">
                    <div className="metric-icon-box icon-bg-success">
                      <i className="fas fa-check-circle"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Link>
        </div>

        {/* Pending Payments */}
        <div className="col-xl-3 col-md-6 mb-4">
          <Link to="/members" className="text-decoration-none">
            <div className="card glass-metric-card border-warning h-100 py-2">
              <div className="card-body">
                <div className="row no-gutters align-items-center">
                  <div className="col mr-2">
                    <div className="text-xs font-weight-bold text-warning text-uppercase mb-1">
                      Awaiting Fees
                    </div>
                    {renderValue(counts.pending_payment)}
                    <div className="text-xs text-muted mt-1">
                      {!loading && counts.total > 0
                        ? `${Math.round((counts.pending_payment / counts.total) * 100)}% pending dues`
                        : 'Outstanding links'}
                    </div>
                  </div>
                  <div className="col-auto">
                    <div className="metric-icon-box icon-bg-warning">
                      <i className="fas fa-clock"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Link>
        </div>
      </div>

      {/* Main Content Grid Sections */}
      <div className="row mt-2">
        {/* Recent Registrations Card */}
        <div className="col-xl-8 col-lg-7 mb-4">
          <div className="dashboard-section-card">
            <h5 className="section-title-creative">
              <i className="fas fa-history mr-2"></i> Recent Registrations Activity
            </h5>
            <div className="activity-table-container">
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="text-muted mt-2">Fetching registration logs...</p>
                </div>
              ) : counts.recent_members.length === 0 ? (
                <div className="text-center py-5">
                  <i className="fas fa-folder-open fa-3x text-gray-300 mb-3"></i>
                  <p className="text-muted">No registration activity logs found.</p>
                </div>
              ) : (
                <table className="activity-table-creative">
                  <thead>
                    <tr>
                      <th>Firm / Member</th>
                      <th>Email / Mobile</th>
                      <th>Registered On</th>
                      <th>Type</th>
                      <th>Payment</th>
                      <th className="text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {counts.recent_members.map((member) => (
                      <tr key={member.id}>
                        <td>
                          <div className="font-weight-bold">{member.name}</div>
                          <span className="text-xs text-muted">ID: #{member.id}</span>
                        </td>
                        <td>
                          <div className="text-xs">{member.email}</div>
                          <div className="text-xs text-muted">{member.mobile || 'No Mobile'}</div>
                        </td>
                        <td>{formatDate(member.created_at)}</td>
                        <td>
                          {member.mem_status === 'New Member' ? (
                            <span className="badge-premium badge-premium-new">New</span>
                          ) : (
                            <span className="badge-premium badge-premium-old">Old</span>
                          )}
                        </td>
                        <td>
                          {member.payment_status === 'paid' ? (
                            <span className="badge-premium badge-premium-paid">Paid</span>
                          ) : member.payment_status === 'pending' ? (
                            <span className="badge-premium badge-premium-pending">Pending</span>
                          ) : (
                            <span className="badge-premium badge-premium-na">Unpaid</span>
                          )}
                        </td>
                        <td className="text-center">
                          <Link
                            to={
                              member.mem_status === 'New Member'
                                ? `/new-member-view/${member.id}`
                                : `/member-view/${member.id}`
                            }
                            className="btn btn-sm btn-outline-primary border-0 rounded-circle"
                            style={{ width: '32px', height: '32px', padding: '4px 0' }}
                            title="Inspect details"
                          >
                            <i className="fas fa-eye"></i>
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        {/* Side Panel: Geographic Distribution & Operations */}
        <div className="col-xl-4 col-lg-5 mb-4">
          {/* Geographical Area stats */}
          <div className="dashboard-section-card mb-4">
            <h5 className="section-title-creative">
              <i className="fas fa-map-marker-alt mr-2"></i> Top Member Locations
            </h5>
            {loading ? (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" variant="primary" />
              </div>
            ) : counts.area_stats.length === 0 ? (
              <div className="text-center py-4 text-muted">
                No location density statistics available.
              </div>
            ) : (
              <div className="py-2">
                {counts.area_stats.map((area, index) => {
                  const totalCount = counts.total || 1;
                  const percent = Math.round((area.count / totalCount) * 100);
                  const color = colors[index % colors.length];
                  return (
                    <div key={area.area} className="area-density-item">
                      <div className="area-density-label">
                        <span>{area.area}</span>
                        <span className="text-muted">
                          {area.count} {area.count === 1 ? 'member' : 'members'} ({percent}%)
                        </span>
                      </div>
                      <div className="area-progress-bg">
                        <div
                          className="area-progress-fill"
                          style={{
                            width: `${percent}%`,
                            backgroundColor: color
                          }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Quick Tasks panel */}

        </div>
      </div>
    </div>
  );
}
